import React, { useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Typography,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  Box,
  Switch,
  Chip,
  Tabs,
  Tab,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Order, OrderStatus, PaymentStatus } from "../../../../Models/Order";
import "./AdminOrderEditableDetailsSection.scss";
import { AdminAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/AdminAPIHandler";
import { ErrorLogger } from "../../../../utils/Logger";
import SnackBar, { SnackTypes } from "../../../UtilityComponents/SnackBar/SnackBar";
import GGLoaderButton from "../../../CommonComponents/GGLoaderButton/GGLoaderButton";
import { formatCurrencyIndian } from "../../../../utils/MoneyConversionUtils";
import GGModalWindow from "../../../UtilityComponents/GGModalWindow/GGModalWindow";
import GGButton from "../../../CommonComponents/GGButton/GGButton";

interface AdminOrderEditableDetailsSectionProps {
  order: Order;
}

const MANUALLY_EDITABLE_PAYMENT_STATUSES = Object.values(PaymentStatus).filter(
  (status) =>
    status !== PaymentStatus.PAYMENT_TO_SELLER_SUCCESS &&
    status !== PaymentStatus.REFUND_TO_BUYER_SUCCESS,
);

const AdminOrderEditableDetailsSection = ({ order }: AdminOrderEditableDetailsSectionProps) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [makePaymentsEntry, setMakePaymentsEntry] = useState<boolean>(false);
  const [updatedOrderStatus, setUpdatedOrderStatus] = useState<OrderStatus>(order.status);
  const [updatedPaymentStatus, setUpdatedPaymentStatus] = useState<PaymentStatus | "">(order.paymentStatus);
  const [summaryTab, setSummaryTab] = useState<number>(0); // 0 for seller, 1 for buyer
  const [showMoreBuyer, setShowMoreBuyer] = useState(false);
  const [showMoreSeller, setShowMoreSeller] = useState(false);
  const [showMoreGig, setShowMoreGig] = useState(false);
  const [showMoreOrder, setShowMoreOrder] = useState(false);
  const [showMoreSummary, setShowMoreSummary] = useState(false);

  const [paymentObject, setPaymentObject] = useState({
    amount: "",
    currency: "INR",
    status: "success",
    method: "netbanking",
    description: "",
    bank: "",
    transactionId: "",
    paymentPurpose: "",
  });

  // Snackbar based status handling
  const snackBarRef = useRef<{ showSnackbar(): any }>(null);
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error,
  });

  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message: message, state: state });
    snackBarRef.current?.showSnackbar();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (makePaymentsEntry) {
        const amount = Number(paymentObject.amount);
        const paymentPurpose = paymentObject.paymentPurpose.trim();
        const method = paymentObject.method.trim();
        const transactionId = paymentObject.transactionId.trim();

        if (!paymentPurpose || !method || !Number.isFinite(amount) || amount <= 0) {
          showSnackbar("Select a payment purpose and method, and enter an amount greater than zero.", SnackTypes.error);
          return;
        }

        if (paymentPurpose === "releaseToSeller" && !transactionId) {
          showSnackbar("Transaction ID is required when releasing payment to the seller.", SnackTypes.error);
          return;
        }

        await AdminAPIHandler.shared.makePaymentEntryForOrderAPI(
          { id: order.id },
          {
            ...paymentObject,
            amount,
            paymentPurpose,
            method,
            transactionId,
            status: "success",
          },
        );
      } else {
        const orderDataToUpdate = {
          ...order,
          status: updatedOrderStatus,
          paymentStatus: updatedPaymentStatus,
        };
        await AdminAPIHandler.shared.updateOrderById(orderDataToUpdate);
      }
      setEditMode(false);
      showSnackbar("Updation done successfully", SnackTypes.success);
    } catch (err) {
      showSnackbar("Updation failed due to error: " + (err as string), SnackTypes.error);
    }
    setLoading(false);
  };

  const handlePaymentChange = (field: keyof typeof paymentObject, value: string) => {
    setPaymentObject((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = async (text: string | number) => {
    try {
      await navigator.clipboard.writeText(text.toString());
      showSnackbar("Copied to clipboard!", SnackTypes.success);
    } catch (error) {
      showSnackbar("Failed to copy!", SnackTypes.error);
    }
  };

  const calculatePayableAmountToSeller = (): string => {
    let grandTotalPaise = order.orderSummary?.orderTotals.totalNetAmountReleasedToSeller ?? 0;
    return formatCurrencyIndian(grandTotalPaise);
  };

  const copyPayableAmountToSeller = () => {
    const grandTotalPaise = order.orderSummary?.orderTotals.totalNetAmountReleasedToSeller ?? 0;
    handleCopyToClipboard((grandTotalPaise / 100).toFixed(2));
  };

  const CopyableField = ({ label, value, onClick }: { label: string; value: string | number; onClick?: () => void }) => (
    <Box className="copyable-field" onClick={onClick || (() => handleCopyToClipboard(value))}>
      <Typography className="field-label">{label}</Typography>
      <Box className="field-value-container">
        <Typography className="field-value">{value}</Typography>
        <ContentCopyIcon className="copy-icon" />
      </Box>
    </Box>
  );

  const renderSellerSummary = () => {
    const summary = order.orderSummary;
    if (!summary) {
      return <Typography variant="body2">No order summary available.</Typography>;
    }

    return (
      <div className="order-summary-breakdown">
        <table className="order-summary-table">
          <tbody>
            {/* Main Order Breakdown */}
            <tr>
              <td className="section-title" colSpan={2}>
                <b>Main Order Breakdown</b>
              </td>
            </tr>
            <tr>
              <td className="item-label">Gig Amount</td>
              <td className="item-value">{formatCurrencyIndian(summary.primaryTransaction.basePrice)}</td>
            </tr>
            <tr>
              <td className="item-label">
                Platform Fee ({(summary.primaryTransaction.sellerFinancials.platformFee.percentage ?? 0) * 100}%)
              </td>
              <td className="item-value deduction">
                - {formatCurrencyIndian(summary.primaryTransaction.sellerFinancials.platformFee.actualFeeCharged)}
              </td>
            </tr>
            <tr>
              <td className="item-label">
                GST @ {(summary.primaryTransaction.sellerFinancials.gstDetails.ratePercentage ?? 0) * 100}%
              </td>
              <td className="item-value deduction">
                - {formatCurrencyIndian(summary.primaryTransaction.sellerFinancials.gstDetails.actualGstAmount)}
              </td>
            </tr>
            {summary.primaryTransaction.sellerFinancials.tcsDetails && (
              <tr>
                <td className="item-label">
                  TCS @ {(summary.primaryTransaction.sellerFinancials.tcsDetails.ratePercentage ?? 0) * 100}%
                </td>
                <td className="item-value deduction">
                  - {formatCurrencyIndian(summary.primaryTransaction.sellerFinancials.tcsDetails.actualTcsAmount)}
                </td>
              </tr>
            )}
            <tr className="subtotal-row">
              <td className="subtotal-label">
                <b>Main Order Subtotal</b>
              </td>
              <td className="subtotal-value">
                <b>{formatCurrencyIndian(summary.primaryTransaction.sellerFinancials.totalAmountPayable)}</b>
              </td>
            </tr>

            {/* Revision Transactions */}
            {summary.revisionTransactions && summary.revisionTransactions.length > 0 && (
              <>
                <tr>
                  <td colSpan={2}>
                    <hr className="section-divider" />
                  </td>
                </tr>
                <tr>
                  <td className="section-title" colSpan={2}>
                    <b>Revision Transactions</b>
                  </td>
                </tr>
                {summary.revisionTransactions.map((revision, index) => {
                  const isFreeRevision = revision.buyerFinancials.totalAmountPayable === 0;
                  const revisionNumber = index + 1;

                  return (
                    <React.Fragment key={revision.transactionId}>
                      <tr className="revision-spacer">
                        <td colSpan={2}></td>
                      </tr>
                      {isFreeRevision ? (
                        <tr>
                          <td className="item-label">Included Revision #{revisionNumber}</td>
                          <td className="item-value free">FREE</td>
                        </tr>
                      ) : (
                        <>
                          <tr>
                            <td className="item-label">Paid Revision #{revisionNumber} - Base Amount</td>
                            <td className="item-value">{formatCurrencyIndian(revision.basePrice)}</td>
                          </tr>
                          <tr>
                            <td className="item-label">
                              Platform Fee ({(revision.sellerFinancials.platformFee.percentage ?? 0) * 100}%)
                            </td>
                            <td className="item-value deduction">
                              - {formatCurrencyIndian(revision.sellerFinancials.platformFee.actualFeeCharged)}
                            </td>
                          </tr>
                          <tr>
                            <td className="item-label">
                              GST @ {(revision.sellerFinancials.gstDetails.ratePercentage ?? 0) * 100}%
                            </td>
                            <td className="item-value deduction">
                              - {formatCurrencyIndian(revision.sellerFinancials.gstDetails.actualGstAmount)}
                            </td>
                          </tr>
                          {revision.sellerFinancials.tcsDetails && (
                            <tr>
                              <td className="item-label">
                                TCS @ {(revision.sellerFinancials.tcsDetails.ratePercentage ?? 0) * 100}%
                              </td>
                              <td className="item-value deduction">
                                - {formatCurrencyIndian(revision.sellerFinancials.tcsDetails.actualTcsAmount)}
                              </td>
                            </tr>
                          )}
                          <tr className="revision-subtotal-row">
                            <td className="revision-subtotal-label">
                              <b>Revision #{revisionNumber} Subtotal</b>
                            </td>
                            <td className="revision-subtotal-value">
                              <b>{formatCurrencyIndian(revision.sellerFinancials.totalAmountPayable)}</b>
                            </td>
                          </tr>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            )}

            <tr>
              <td colSpan={2}>
                <hr className="grand-total-divider" />
              </td>
            </tr>
            <tr className="grand-total-row">
              <td className="grand-total-label">
                <b>Total Amount to be Credited</b>
              </td>
              <td className="grand-total-value">
                <b>{formatCurrencyIndian(summary.orderTotals.totalNetAmountReleasedToSeller)}</b>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderBuyerSummary = () => {
    const summary = order.orderSummary;
    if (!summary) {
      return <Typography variant="body2">No order summary available.</Typography>;
    }

    return (
      <div className="order-summary-breakdown">
        <table className="order-summary-table">
          <tbody>
            {/* Main Order Breakdown */}
            <tr>
              <td className="section-title" colSpan={2}>
                <b>Main Order Breakdown</b>
              </td>
            </tr>
            <tr>
              <td className="item-label">Base Price</td>
              <td className="item-value">{formatCurrencyIndian(summary.primaryTransaction.basePrice)}</td>
            </tr>
            {summary.primaryTransaction.promoDetails && (
              <tr>
                <td className="item-label">
                  Promo Discount
                  {summary.primaryTransaction.promoDetails.promoCode && ` (${summary.primaryTransaction.promoDetails.promoCode})`}
                </td>
                <td className="item-value deduction">
                  - {formatCurrencyIndian(summary.primaryTransaction.promoDetails.actualDiscountValue)}
                </td>
              </tr>
            )}
            <tr>
              <td className="item-label">
                Platform Fee ({(summary.primaryTransaction.buyerFinancials.platformFee.percentage ?? 0) * 100}%)
              </td>
              <td className="item-value">
                {formatCurrencyIndian(summary.primaryTransaction.buyerFinancials.platformFee.actualFeeCharged)}
              </td>
            </tr>
            <tr>
              <td className="item-label">
                GST @ {(summary.primaryTransaction.buyerFinancials.gstDetails.ratePercentage ?? 0) * 100}%
              </td>
              <td className="item-value">
                {formatCurrencyIndian(summary.primaryTransaction.buyerFinancials.gstDetails.actualGstAmount)}
              </td>
            </tr>
            <tr className="subtotal-row">
              <td className="subtotal-label">
                <b>Main Order Subtotal</b>
              </td>
              <td className="subtotal-value">
                <b>{formatCurrencyIndian(summary.primaryTransaction.buyerFinancials.totalAmountPayable)}</b>
              </td>
            </tr>

            {/* Revision Transactions */}
            {summary.revisionTransactions && summary.revisionTransactions.length > 0 && (
              <>
                <tr>
                  <td colSpan={2}>
                    <hr className="section-divider" />
                  </td>
                </tr>
                <tr>
                  <td className="section-title" colSpan={2}>
                    <b>Revision Transactions</b>
                  </td>
                </tr>
                {summary.revisionTransactions.map((revision, index) => {
                  const isFreeRevision = revision.buyerFinancials.totalAmountPayable === 0;
                  const revisionNumber = index + 1;

                  return (
                    <React.Fragment key={revision.transactionId}>
                      <tr className="revision-spacer">
                        <td colSpan={2}></td>
                      </tr>
                      {isFreeRevision ? (
                        <tr>
                          <td className="item-label">Included Revision #{revisionNumber}</td>
                          <td className="item-value free">FREE</td>
                        </tr>
                      ) : (
                        <>
                          <tr>
                            <td className="item-label">Paid Revision #{revisionNumber} - Base Amount</td>
                            <td className="item-value">{formatCurrencyIndian(revision.basePrice)}</td>
                          </tr>
                          {revision.promoDetails && (
                            <tr>
                              <td className="item-label">
                                Promo Discount
                                {revision.promoDetails.promoCode && ` (${revision.promoDetails.promoCode})`}
                              </td>
                              <td className="item-value deduction">
                                - {formatCurrencyIndian(revision.promoDetails.actualDiscountValue)}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td className="item-label">
                              Platform Fee ({(revision.buyerFinancials.platformFee.percentage ?? 0) * 100}%)
                            </td>
                            <td className="item-value">
                              {formatCurrencyIndian(revision.buyerFinancials.platformFee.actualFeeCharged)}
                            </td>
                          </tr>
                          <tr>
                            <td className="item-label">
                              GST @ {(revision.buyerFinancials.gstDetails.ratePercentage ?? 0) * 100}%
                            </td>
                            <td className="item-value">
                              {formatCurrencyIndian(revision.buyerFinancials.gstDetails.actualGstAmount)}
                            </td>
                          </tr>
                          <tr className="revision-subtotal-row">
                            <td className="revision-subtotal-label">
                              <b>Revision #{revisionNumber} Subtotal</b>
                            </td>
                            <td className="revision-subtotal-value">
                              <b>{formatCurrencyIndian(revision.buyerFinancials.totalAmountPayable)}</b>
                            </td>
                          </tr>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            )}

            <tr>
              <td colSpan={2}>
                <hr className="grand-total-divider" />
              </td>
            </tr>
            <tr className="grand-total-row">
              <td className="grand-total-label">
                <b>Total Amount Paid</b>
              </td>
              <td className="grand-total-value">
                <b>{formatCurrencyIndian(summary.orderTotals.totalAmountPayableByBuyer)}</b>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="admin-editable-details-section">
      <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={5000} message={snackBarParams.message} />

      <Accordion key={order.id}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="order-summary-header">
          <div className="order-title-section">
            <Typography variant="h6" className="order-id-title">
              Order #{order.id}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Status: <span style={{ fontWeight: 600, color: '#1976d2' }}>{order.status}</span>
                {order.orderCompletionTimeStamp && (() => {
                  const now = new Date();
                  const completed = new Date(order.orderCompletionTimeStamp);
                  const hoursDiff = (now.getTime() - completed.getTime()) / (1000 * 60 * 60);
                  return hoursDiff < 24 ? (
                    <span style={{ marginLeft: '8px', color: '#ff9800', fontWeight: 600, fontSize: '0.85rem' }}>
                      ⏱️ {Math.floor(hoursDiff)}h ago
                    </span>
                  ) : null;
                })()}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Payment Status: <span style={{ fontWeight: 600, color: order.paymentStatus.includes("SUCCESS") ? '#2e7d32' : '#ed6c02' }}>{order.paymentStatus}</span>
              </Typography>
            </Box>
          </div>

          <Box className="edit-toggle">
            <GGButton
              btnColor="#000000"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setEditMode(true);
              }}
            >
              Edit Order
            </GGButton>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <div className="order-details-container">
            {/* Order Info Section */}
            <Box className="info-section">
              <Box className="section-header-with-toggle">
                <Typography variant="h6" className="section-header">
                  Order Information
                </Typography>
                <IconButton size="small" onClick={() => setShowMoreOrder(!showMoreOrder)}>
                  {showMoreOrder ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Box className="info-grid">
                <CopyableField label="Order ID" value={order.id} />
                <CopyableField label="Status" value={order.status} />
                <CopyableField label="Payment Status" value={order.paymentStatus} />
              </Box>
              <Collapse in={showMoreOrder}>
                <Box className="info-grid" sx={{ mt: 2 }}>
                  <CopyableField label="Action Card ID" value={order.actionCard} />
                  <CopyableField label="Revisions Included" value={order.noOfRevisionsIncluded} />
                  <CopyableField label="Deliverable Types" value={order.deliverableTypes.join(", ")} />
                  <CopyableField label="Channel ID" value={order.metaData.channelId} />
                  <CopyableField label="Approved On" value={order.orderCompletionTimeStamp ? new Date(order.orderCompletionTimeStamp).toLocaleString() : "N/A"} />
                </Box>

                {/* Payment IDs */}
                <Typography variant="subtitle1" className="subsection-header" sx={{ mt: 2 }}>
                  Payment References
                </Typography>
                <Box className="info-grid">
                  <CopyableField label="Received Payment ID" value={order.paymentMeta?.recievedPaymentId ?? "N/A"} />
                  <CopyableField label="Released Payment ID" value={order.paymentMeta?.releasedPaymentId ?? "N/A"} />
                  <CopyableField label="Refund Payment ID" value={order.paymentMeta?.refundPaymentId ?? "N/A"} />
                </Box>

                {/* Delivery Details */}
                <Typography variant="subtitle1" className="subsection-header" sx={{ mt: 2 }}>
                  Delivery Details
                </Typography>
                <Box className="info-grid">
                  <CopyableField label="Delivery ID" value={order.deliveryId ?? "N/A"} />
                  <CopyableField label="Delivery Date" value={order.deliveryTimeStamp != null ? new Date(order.deliveryTimeStamp).toLocaleString() : "N/A"} />
                </Box>
              </Collapse>
            </Box>

            {/* Buyer Info Section */}
            <Box className="info-section">
              <Box className="section-header-with-toggle">
                <Typography variant="h6" className="section-header">
                  Buyer Information
                </Typography>
                <IconButton size="small" onClick={() => setShowMoreBuyer(!showMoreBuyer)}>
                  {showMoreBuyer ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Box className="info-grid">
                <CopyableField label="Name" value={order.metaData.buyer.fullName} />
                <CopyableField label="Email" value={order.metaData.buyer.email} />
                <CopyableField label="Buyer ID" value={order.metaData.buyer.id} />
              </Box>
              <Collapse in={showMoreBuyer}>
                <Box className="info-grid" sx={{ mt: 2 }}>
                  <CopyableField label="Mobile No" value={order.metaData.buyer.mobileNo || "N/A"} />
                  <CopyableField label="Profile Image" value={order.metaData.buyer.profileImage || "No image"} />
                  <CopyableField label="About" value={order.metaData.buyer.about || "N/A"} />
                  <CopyableField label="Created At" value={order.metaData.buyer.createdAt} />
                  <CopyableField label="Chat Token" value={order.metaData.buyer.chatToken} />
                </Box>
              </Collapse>
            </Box>

            {/* Seller Info Section */}
            <Box className="info-section">
              <Box className="section-header-with-toggle">
                <Typography variant="h6" className="section-header">
                  Seller Information
                </Typography>
                <IconButton size="small" onClick={() => setShowMoreSeller(!showMoreSeller)}>
                  {showMoreSeller ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Box className="info-grid">
                <CopyableField label="Name" value={order.metaData.seller.fullName} />
                <CopyableField label="Email" value={order.metaData.seller.email} />
                <CopyableField label="Seller ID" value={order.metaData.seller.id} />
              </Box>
              <Collapse in={showMoreSeller}>
                <Box className="info-grid" sx={{ mt: 2 }}>
                  <CopyableField label="Occupation" value={order.metaData.seller.occupation} />
                  <CopyableField label="Contact No" value={order.metaData.seller.contactNumber} />
                  <CopyableField
                    label="Location"
                    value={`${order.metaData.seller.location.region}, ${order.metaData.seller.location.state}, ${order.metaData.seller.location.country}`}
                  />
                  <CopyableField label="Languages" value={order.metaData.seller.location.languages.join(", ")} />
                  <CopyableField label="Gender" value={order.metaData.seller.gender} />
                  <CopyableField label="Date of Birth" value={order.metaData.seller.dateOfBirth} />
                  <CopyableField label="User Verified" value={order.metaData.seller.userVerified ? "Yes" : "No"} />
                </Box>
              </Collapse>
            </Box>

            {/* Gig Info Section */}
            <Box className="info-section">
              <Box className="section-header-with-toggle">
                <Typography variant="h6" className="section-header">
                  Gig Information
                </Typography>
                <IconButton size="small" onClick={() => setShowMoreGig(!showMoreGig)}>
                  {showMoreGig ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Box className="info-grid">
                <CopyableField label="Title" value={order.metaData.gig.title} />
                <CopyableField label="Gig ID" value={order.metaData.gig.id} />
                <CopyableField label="Subcategory" value={order.metaData.gig.subcategory} />
              </Box>
              <Collapse in={showMoreGig}>
                <Box className="info-grid" sx={{ mt: 2 }}>
                  <CopyableField label="Description" value={order.metaData.gig.description} />
                  <CopyableField label="Min Price" value={`₹${order.metaData.gig.minPrice}`} />
                  <CopyableField label="Rating" value={order.metaData.gig.rating || "N/A"} />
                </Box>
              </Collapse>
            </Box>

            {/* Order Summary Section with Tabs */}
            <Box className="info-section order-summary-section">
              <Box className="section-header-with-toggle">
                <Typography variant="h6" className="section-header">
                  Order Summary
                </Typography>
                <IconButton size="small" onClick={() => setShowMoreSummary(!showMoreSummary)}>
                  {showMoreSummary ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              
              {/* Show only totals when collapsed */}
              {!showMoreSummary && (
                <Box className="summary-totals-preview">
                  <Box className="total-preview-item seller">
                    <Typography className="total-label">Seller - Total Amount to be Credited</Typography>
                    <Typography className="total-value">
                      {formatCurrencyIndian(order.orderSummary?.orderTotals.totalNetAmountReleasedToSeller ?? 0)}
                    </Typography>
                  </Box>
                  <Box className="total-preview-item buyer">
                    <Typography className="total-label">Buyer - Total Amount Paid</Typography>
                    <Typography className="total-value">
                      {formatCurrencyIndian(order.orderSummary?.orderTotals.totalAmountPayableByBuyer ?? 0)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Show full breakdown when expanded */}
              <Collapse in={showMoreSummary}>
                <Tabs value={summaryTab} onChange={(e, newValue) => setSummaryTab(newValue)} className="summary-tabs">
                  <Tab label="Seller Breakdown" />
                  <Tab label="Buyer Breakdown" />
                </Tabs>
                <Box className="summary-tab-content">
                  {summaryTab === 0 && renderSellerSummary()}
                  {summaryTab === 1 && renderBuyerSummary()}
                </Box>
              </Collapse>
            </Box>

            {/* Seller Payment Details */}
            <Box className="info-section">
              <Typography variant="h6" className="section-header">
                Seller Payment Methods
              </Typography>
              {(order.metaData.seller.paymentDetails?.length ?? 0) > 0 ? (
                <Box className="payment-methods-container">
                  {order.metaData.seller.paymentDetails?.map((payment, index) => {
                    let paymentDetailsObj = null;
                    let displayInfo: any = { type: 'unknown', details: 'N/A' };
                    
                    try {
                      if (payment.paymentDetails) {
                        paymentDetailsObj = JSON.parse(payment.paymentDetails);
                        
                        // Handle different payment method types
                        if (payment.paymentMethod === 'bankAccount' || payment.paymentMethod.toLowerCase() === 'bank' || payment.paymentMethod.toLowerCase() === 'netbanking') {
                          if (paymentDetailsObj && typeof paymentDetailsObj === 'object' && !Array.isArray(paymentDetailsObj)) {
                            displayInfo = {
                              type: 'bank',
                              holderName: paymentDetailsObj.holderName || 'N/A',
                              accountNumber: paymentDetailsObj.accountNumber || paymentDetailsObj.account_number || 'N/A',
                              bankName: paymentDetailsObj.bankName || paymentDetailsObj.bank_name || 'N/A',
                              ifsc: paymentDetailsObj.bankIFSC || paymentDetailsObj.ifsc || paymentDetailsObj.IFSC || 'N/A',
                            };
                          } else {
                            displayInfo = { type: 'bank', details: String(paymentDetailsObj) };
                          }
                        } else if (payment.paymentMethod === 'upi' || payment.paymentMethod.toLowerCase() === 'upi') {
                          // For UPI, the paymentDetails is directly the UPI ID as a string
                          displayInfo = {
                            type: 'upi',
                            vpa: typeof paymentDetailsObj === 'string' 
                              ? paymentDetailsObj 
                              : (paymentDetailsObj?.vpa || paymentDetailsObj?.upiId || paymentDetailsObj?.upi_id || 'N/A'),
                          };
                        } else {
                          displayInfo = {
                            type: 'other',
                            details: (paymentDetailsObj && typeof paymentDetailsObj === 'object') 
                              ? JSON.stringify(paymentDetailsObj) 
                              : String(paymentDetailsObj)
                          };
                        }
                      }
                    } catch (error) {
                      ErrorLogger.log(error, 'AdminOrderEditableDetailsSection.parsePaymentDetails');
                      displayInfo = { type: 'error', details: payment.paymentDetails || "N/A" };
                    }

                    const getCopyText = () => {
                      if (displayInfo.type === 'bank') {
                        return `Account: ${displayInfo.accountNumber}, IFSC: ${displayInfo.ifsc}, Holder: ${displayInfo.holderName}, Bank: ${displayInfo.bankName}`;
                      } else if (displayInfo.type === 'upi') {
                        return displayInfo.vpa;
                      }
                      return displayInfo.details;
                    };

                    return (
                      <Box 
                        key={index} 
                        className={`payment-method-row ${payment.isDefault ? 'default' : 'non-default'}`}
                        onClick={() => handleCopyToClipboard(getCopyText())}
                      >
                        <Box className="payment-method-info">
                          <Box className="method-name-container">
                            <Typography className="method-name">
                              {payment.paymentMethod.toUpperCase()}
                            </Typography>
                            {payment.isDefault && (
                              <Chip label="DEFAULT" size="small" className="default-badge" />
                            )}
                          </Box>
                          
                          {/* Display based on payment type */}
                          {displayInfo.type === 'bank' ? (
                            <Box className="bank-details">
                              <Typography className="bank-detail-row">
                                <span className="detail-label">A/C:</span> {displayInfo.accountNumber}
                              </Typography>
                              <Typography className="bank-detail-row">
                                <span className="detail-label">IFSC:</span> {displayInfo.ifsc}
                              </Typography>
                              <Typography className="bank-detail-row">
                                <span className="detail-label">Name:</span> {displayInfo.holderName}
                              </Typography>
                              <Typography className="bank-detail-row">
                                <span className="detail-label">Bank:</span> {displayInfo.bankName}
                              </Typography>
                            </Box>
                          ) : displayInfo.type === 'upi' ? (
                            <Typography className="method-id">
                              {displayInfo.vpa}
                            </Typography>
                          ) : (
                            <Typography className="method-id">
                              {displayInfo.details}
                            </Typography>
                          )}
                        </Box>
                        <Box className="copy-indicator">
                          <ContentCopyIcon className="copy-icon-small" />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No payment details available.
                </Typography>
              )}
            </Box>

            {/* Payable Amount Highlight */}
            <Box
              className="payable-amount-box"
              role="button"
              tabIndex={0}
              aria-label="Copy amount payable to seller"
              onClick={copyPayableAmountToSeller}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  copyPayableAmountToSeller();
                }
              }}
            >
              <Typography variant="h5" className="payable-label">
                Amount Payable to Seller
              </Typography>
              <Typography variant="h4" className="payable-amount">
                {calculatePayableAmountToSeller()}
              </Typography>
              <ContentCopyIcon className="copy-icon-small" />
            </Box>

            <GGModalWindow
              open={editMode}
              onCloseHandler={() => setEditMode(false)}
              title="Edit Order"
              width="min(720px, 90vw)"
              childView={
              <div className="edit-section">
                <Typography variant="h5" className="edit-section-title">
                  Edit Order Details
                </Typography>

                {/* Order Status Dropdown */}
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel>Order Status</InputLabel>
                  <Select
                    value={updatedOrderStatus}
                    onChange={(e) => setUpdatedOrderStatus(e.target.value as OrderStatus)}
                    label="Order Status"
                    disabled={makePaymentsEntry}
                  >
                    {Object.values(OrderStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Payment Status Dropdown */}
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel>Order Payment Status</InputLabel>
                  <Select
                    value={updatedPaymentStatus}
                    onChange={(e) => setUpdatedPaymentStatus(e.target.value as PaymentStatus)}
                    label="Order Payment Status"
                    disabled={makePaymentsEntry}
                  >
                    {MANUALLY_EDITABLE_PAYMENT_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Payment Details Section */}
                <Box className="payment-entry-toggle">
                  <Typography>
                    <b>Make a payment entry?</b>
                  </Typography>
                  <Switch color="success" checked={makePaymentsEntry} onChange={() => setMakePaymentsEntry((prev) => !prev)} />
                </Box>

                {makePaymentsEntry && (
                  <Typography variant="body2" color="text.secondary">
                    The payment purpose determines the final order and payment status after a successful entry.
                  </Typography>
                )}

                {makePaymentsEntry && (
                  <Box className="payment-details-edit">
                    <Typography variant="h6" gutterBottom>
                      Add Payment Details
                    </Typography>
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel>Payment Purpose</InputLabel>
                      <Select value={paymentObject.paymentPurpose} label="Payment Purpose" onChange={(e) => handlePaymentChange("paymentPurpose", e.target.value)}>
                        <MenuItem value="releaseToSeller">Release To Seller</MenuItem>
                        <MenuItem value="refundToBuyer">Refund To Buyer</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Amount (in Rupees)"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      value={paymentObject.amount}
                      onChange={(e) => handlePaymentChange("amount", e.target.value)}
                    />
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel>Currency</InputLabel>
                      <Select value={paymentObject.currency} disabled label="Currency">
                        <MenuItem value="INR">INR</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel>Payment Status</InputLabel>
                      <Select value="success" disabled label="Payment Status">
                        <MenuItem value="success">Success</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel>Payment Method</InputLabel>
                      <Select value={paymentObject.method} label="Payment Method" onChange={(e) => handlePaymentChange("method", e.target.value)}>
                        <MenuItem value="netbanking">Netbanking</MenuItem>
                        <MenuItem value="upi">UPI</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Description"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      value={paymentObject.description}
                      onChange={(e) => handlePaymentChange("description", e.target.value)}
                    />
                    <TextField
                      label="Bank"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      value={paymentObject.bank}
                      onChange={(e) => handlePaymentChange("bank", e.target.value)}
                    />
                    <TextField
                      label={paymentObject.paymentPurpose === "releaseToSeller" ? "Transaction ID *" : "Transaction ID"}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      value={paymentObject.transactionId}
                      onChange={(e) => handlePaymentChange("transactionId", e.target.value)}
                    />
                  </Box>
                )}

                {/* Submit Button */}
                <GGLoaderButton fullWidth isLoading={loading} onClick={handleSubmit} sx={{ mt: 3 }}>
                  Submit Changes
                </GGLoaderButton>
              </div>
              }
            />
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default AdminOrderEditableDetailsSection;
